import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const CHANNEL_COLORS = {
    sms: '#8B5CF6',
    email: '#EC4899',
    whatsapp: '#22c55e',
    browser: '#3B82F6',
};

const tooltipStyle = {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#e2e8f0',
    fontSize: 12,
};

// BUG-107: Format the last-event timestamp passed from the dashboard's
// meta block. Empty string (or a value that doesn't parse) means the
// underlying source has no rows — show "No data yet" so the user can
// distinguish that from "fresh data is coming in" at a glance.
function formatLastEvent(ts) {
    if (!ts) return 'No data yet';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return 'No data yet';
    return `Last activity: ${d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })}`;
}

export default function ChannelActivityCard({ data, lastEventAt }) {
    const chartData = Object.entries(data).map(([channel, stats]) => ({
        channel: channel.charAt(0).toUpperCase() + channel.slice(1),
        channelKey: channel,
        delivered: stats.delivered,
        failed: stats.failed,
    }));

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>Channel Activity</h3>
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 8, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis
                        type="category"
                        dataKey="channel"
                        tick={{ fill: '#CBD5E1', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={80}
                    />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                    <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12, color: '#CBD5E1', paddingTop: 8 }}
                    />
                    <Bar dataKey="delivered" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="failed" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
            </ResponsiveContainer>
            {/* Per-channel color dots legend */}
            <div style={styles.channelLegend}>
                {chartData.map(d => (
                    <div key={d.channelKey} style={styles.channelItem}>
                        <span style={{ ...styles.dot, backgroundColor: CHANNEL_COLORS[d.channelKey] || '#64748B' }} />
                        <span style={styles.channelLabel}>{d.channel}</span>
                        <span style={styles.channelCount}>{d.delivered + d.failed} sent</span>
                    </div>
                ))}
            </div>
            {/* BUG-107: tiny diagnostic so empty cards are unambiguous */}
            <div style={styles.diagnostic}>{formatLastEvent(lastEventAt)}</div>
        </div>
    );
}

const styles = {
    card: {
        background: '#1e3a5f',
        border: '1px solid #2d4a6f',
        borderRadius: 8,
        padding: 20,
    },
    title: {
        margin: 0,
        marginBottom: 8,
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
    },
    channelLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginTop: 8,
    },
    channelItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        display: 'inline-block',
    },
    channelLabel: {
        fontSize: 12,
        color: '#CBD5E1',
    },
    channelCount: {
        fontSize: 11,
        color: '#64748B',
    },
    diagnostic: {
        marginTop: 8,
        fontSize: 11,
        color: '#64748B',
        fontStyle: 'italic',
    },
};
